import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - TS 4.9.5 can't resolve all exports from lucide-react's large barrel d.ts
import PackageCheck from 'lucide-react/dist/esm/icons/package-check';
// @ts-ignore
import LogOut from 'lucide-react/dist/esm/icons/log-out';
// @ts-ignore
import Package from 'lucide-react/dist/esm/icons/package';
// @ts-ignore
import Truck from 'lucide-react/dist/esm/icons/truck';
// @ts-ignore
import ArrowDownToLine from 'lucide-react/dist/esm/icons/arrow-down-to-line';
// @ts-ignore
import Plus from 'lucide-react/dist/esm/icons/plus';
// @ts-ignore
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
// @ts-ignore
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
// @ts-ignore
import Clock from 'lucide-react/dist/esm/icons/clock';
// @ts-ignore
import Building2 from 'lucide-react/dist/esm/icons/building-2';
// @ts-ignore
import User from 'lucide-react/dist/esm/icons/user';
// @ts-ignore
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
// @ts-ignore
import CloudOff from 'lucide-react/dist/esm/icons/cloud-off';
// @ts-ignore
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
// @ts-ignore
import WifiOff from 'lucide-react/dist/esm/icons/wifi-off';
// @ts-ignore
import Home from 'lucide-react/dist/esm/icons/home';
import {
  getConferenteUser, logoutConferente, getConferenteMe,
  listarPaletesConferente, getConferenteLoginUrl
} from '../../services/conferente';
import type { PaleteEmAndamento } from '../../services/conferente';
import PWAInstallPrompt from '../../components/PWA/PWAInstallPrompt';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useOfflineMode } from '../../hooks/useOfflineMode';
import { contarLeiturasPendentes, contarConferenciasPendentes, sincronizarPendentes, listarConferenciasPendentes } from '../../services/offlineQueue';

const TIPO_LABELS: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string; desc: string }> = {
  pre_carregamento: {
    label: 'Pré-Carregamento',
    icon: <Package className="w-8 h-8" />,
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    desc: 'Montagem dos paletes no distribuidor'
  },
  carregamento: {
    label: 'Carregamento',
    icon: <Truck className="w-8 h-8" />,
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    desc: 'Paletes sendo carregados no veículo'
  },
  descarregamento: {
    label: 'Descarregamento',
    icon: <ArrowDownToLine className="w-8 h-8" />,
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200 hover:bg-green-100',
    desc: 'Descarga no destino final'
  }
};

const CONFERENTE_USER_KEY = 'conferente_user';

const ConferenteDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getConferenteUser());
  const isOnline = useOnlineStatus();
  const [conferenciasOffline, setConferenciasOffline] = useState<any[]>([]);
  const [paletesEmAndamento, setPaletesEmAndamento] = useState<PaleteEmAndamento[]>([]);
  const [totalAtivas, setTotalAtivas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [pendentesCount, setPendentesCount] = useState(0);
  const [confPendentesCount, setConfPendentesCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [clientesExpandidos, setClientesExpandidos] = useState<Record<string, boolean>>({});
  useOfflineMode(); // Auto-sync de dados mestres em background

  const carregarDados = useCallback(async () => {
    try {
      setErro('');
      const [meResult, paletesResult] = await Promise.all([
        getConferenteMe(),
        listarPaletesConferente().catch(() => null)
      ]);
      setTotalAtivas(meResult.conferencias_ativas);

      // Atualizar localStorage e state com dados atualizados do servidor
      if (meResult.usuario) {
        const currentUser = getConferenteUser();
        const updatedUser = { ...currentUser, ...meResult.usuario };
        localStorage.setItem(CONFERENTE_USER_KEY, JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      if (paletesResult?.paletes) {
        // Filtrar apenas paletes com bipagens iniciadas mas incompletas
        const incompletos = paletesResult.paletes.filter(
          p => p.total_volumes > 0 && p.volumes_faltantes > 0
        );
        setPaletesEmAndamento(incompletos);
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      setErro(error.message || 'Erro ao carregar dados. Verifique sua conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Check pending offline readings and conferences count, and auto-sync if online
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      contarLeiturasPendentes().catch(() => 0),
      contarConferenciasPendentes().catch(() => 0),
      listarConferenciasPendentes().catch(() => [] as any[]),
    ]).then(([leitCount, confCount, pendentes]) => {
      if (cancelled) return;
      setPendentesCount(leitCount);
      setConfPendentesCount(confCount);
      setConferenciasOffline(pendentes.filter(c => c.status !== 'sincronizado'));
      // Auto-sync when online and there are pending items
      if (navigator.onLine && (leitCount > 0 || confCount > 0)) {
        handleSync();
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing, isOnline]);

  // Ocultar rodapé fixo quando o scroll chegar ao final
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      // Considera "no final" quando faltam menos de 20px
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Verificar estado inicial
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await sincronizarPendentes();
      if (result.sincronizados > 0 || result.conferencias_criadas > 0) {
        await carregarDados();
      }
      const leitCount = await contarLeiturasPendentes();
      const confCount = await contarConferenciasPendentes();
      setPendentesCount(leitCount);
      setConfPendentesCount(confCount);
    } catch (err: any) {
      console.error('Erro ao sincronizar:', err);
      setErro('Erro ao sincronizar dados. Verifique sua conexão.');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    const loginUrl = getConferenteLoginUrl();
    await logoutConferente();
    navigate(loginUrl, { replace: true });
  };

  const handleNovaConferencia = (tipo: string) => {
    // Cliente não é mais obrigatório - o palete já identifica o cliente automaticamente
    navigate(`/conferente/${tipo}`);
  };

  const toggleCliente = (clienteKey: string) => {
    setClientesExpandidos(prev => ({ ...prev, [clienteKey]: !prev[clienteKey] }));
  };

  // Agrupar paletes por cliente
  const paletesAgrupados = React.useMemo(() => {
    const grupos: Record<string, { nome: string; paletes: PaleteEmAndamento[] }> = {};
    for (const palete of paletesEmAndamento) {
      const key = palete.cliente_id ? String(palete.cliente_id) : 'sem_cliente';
      if (!grupos[key]) {
        grupos[key] = {
          nome: palete.cliente_razao_social || 'Cliente não identificado',
          paletes: []
        };
      }
      grupos[key].paletes.push(palete);
    }
    // Ordenar por nome do cliente
    return Object.entries(grupos).sort((a, b) =>
      a[1].nome.localeCompare(b[1].nome, 'pt-BR')
    );
  }, [paletesEmAndamento]);

  // Verificar se é conferente novo aguardando aprovação
  const isConferenteNovo = user?.is_conferente || user?.tipo === 'conferente_novo';
  const aguardandoAprovacao = isConferenteNovo && user?.status === 'pendente';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Tela de aguardando aprovação
  if (aguardandoAprovacao) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PWAInstallPrompt />
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-semibold text-base">{user?.nome || 'Conferente'}</h1>
                <p className="text-green-100 text-xs">CPF: {user?.cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') || ''}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="p-2 rounded-lg bg-white bg-opacity-10 hover:bg-opacity-20">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Conteúdo */}
        <div className="p-6 flex flex-col items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2 text-center">Aguardando Aprovacao</h2>
          <p className="text-gray-500 text-sm text-center max-w-xs">
            Seu cadastro esta aguardando aprovacao. Assim que um gestor liberar seu acesso, as cargas disponiveis aparecerão aqui.
          </p>
          <button
            onClick={() => { carregarDados(); }}
            className="mt-6 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Verificar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PWAInstallPrompt />

      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-blue-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOff className="w-4 h-4" />
          Sem conexao - conferencias serao salvas localmente
        </div>
      )}

      {/* Pending Sync Banner */}
      {(pendentesCount > 0 || confPendentesCount > 0) && isOnline && (
        <div className="bg-blue-500 text-white px-4 py-2.5 flex items-center justify-between text-sm">
          <span>
            {confPendentesCount > 0 && `${confPendentesCount} conferência${confPendentesCount > 1 ? 's' : ''}`}
            {confPendentesCount > 0 && pendentesCount > 0 && ' e '}
            {pendentesCount > 0 && `${pendentesCount} leitura${pendentesCount > 1 ? 's' : ''}`}
            {' '}pendente{(pendentesCount + confPendentesCount) > 1 ? 's' : ''}
          </span>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition active:bg-white/40"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-green-600 text-white px-4 py-4 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-base">{user?.nome || 'Conferente'}</p>
              <p className="text-green-200 text-sm">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(user?.tipo === 'admin' || user?.tipo === 'supervisor') && (
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 hover:bg-white/10 rounded-lg transition active:bg-white/20"
                title="Painel Administrativo"
                aria-label="Abrir painel administrativo"
              >
                <Home className="w-6 h-6" />
              </a>
            )}
            <button
              onClick={handleLogout}
              className="p-2.5 hover:bg-white/10 rounded-lg transition active:bg-white/20"
              title="Sair"
              aria-label="Sair do sistema"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-3 flex gap-3">
          <div className="flex-1 bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{totalAtivas}</p>
            <p className="text-sm text-green-200">Conferências</p>
          </div>
          <div className="flex-1 bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{paletesEmAndamento.length}</p>
            <p className="text-sm text-green-200">Paletes pendentes</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 pb-24">
        {erro && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-red-700">{erro}</p>
            <button
              onClick={() => { setLoading(true); carregarDados(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition flex-shrink-0 active:bg-red-800"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Nova Conferência */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-600" />
            Nova Conferência
          </h2>

          {!isOnline && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 flex items-center gap-2">
              <WifiOff className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-700">Sem conexao. Conferencias serao salvas localmente e sincronizadas ao reconectar.</p>
            </div>
          )}


          <div className="space-y-3">
            {Object.entries(TIPO_LABELS).map(([tipo, config]) => (
              <button
                key={tipo}
                onClick={() => handleNovaConferencia(tipo)}
                className={`w-full p-4 rounded-xl border-2 ${config.bg} transition-all active:scale-[0.98] flex items-center gap-4`}
              >
                <div className={config.color}>
                  {config.icon}
                </div>
                <div className="flex-1 text-left">
                  <p className={`font-semibold ${config.color}`}>{config.label}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{config.desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        </div>

        {/* Conferências Offline */}
        {conferenciasOffline.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <CloudOff className="w-5 h-5 text-blue-600" />
              Conferencias Offline
            </h2>
            <div className="space-y-2">
              {conferenciasOffline.map(conf => {
                const tipoInfo = TIPO_LABELS[conf.tipo] || TIPO_LABELS.carregamento;
                const isFalha = conf.status === 'falha_permanente';
                return (
                  <button
                    key={conf.temp_id}
                    onClick={() => navigate(`/conferente/conferencia/${conf.temp_id}`)}
                    className={`w-full rounded-xl p-4 border transition text-left active:scale-[0.98] ${
                      isFalha
                        ? 'bg-red-50 border-red-200 hover:border-red-300'
                        : 'bg-blue-50 border-blue-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${tipoInfo.color}`}>
                          {tipoInfo.label}
                        </span>
                        {isFalha ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Erro no sync
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Offline
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    {isFalha && conf.erro_msg && (
                      <p className="text-xs text-red-600 mb-1">{conf.erro_msg}</p>
                    )}
                    {conf.placa_veiculo && (
                      <p className="text-xs text-gray-500">
                        <Truck className="w-3 h-3 inline mr-1" />
                        {conf.placa_veiculo}
                      </p>
                    )}
                    <p className={`text-xs mt-1 ${isFalha ? 'text-red-400' : 'text-blue-400'}`}>
                      Criada em {new Date(conf.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Paletes em Andamento (bipagens incompletas) - agrupados por cliente */}
        {paletesEmAndamento.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Em Andamento
            </h2>

            <div className="space-y-3">
              {paletesAgrupados.map(([clienteKey, grupo]) => {
                const isExpandido = clientesExpandidos[clienteKey] !== false; // expandido por padrão
                const totalFaltantes = grupo.paletes.reduce((sum, p) => sum + p.volumes_faltantes, 0);

                return (
                  <div key={clienteKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Cabeçalho do cliente (colapsável) */}
                    <button
                      onClick={() => toggleCliente(clienteKey)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition text-left active:bg-gray-100"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm font-semibold text-gray-800 truncate">{grupo.nome}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                          {grupo.paletes.length} palete{grupo.paletes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {totalFaltantes > 0 && (
                          <span className="text-xs text-orange-600">{totalFaltantes} vol. faltantes</span>
                        )}
                        {isExpandido
                          ? <ChevronDown className="w-4 h-4 text-gray-400" />
                          : <ChevronRight className="w-4 h-4 text-gray-400" />
                        }
                      </div>
                    </button>

                    {/* Lista de paletes do cliente */}
                    {isExpandido && (
                      <div className="divide-y divide-gray-100">
                        {grupo.paletes.map(palete => {
                          const progresso = palete.total_esperado > 0
                            ? Math.round((palete.total_volumes / palete.total_esperado) * 100)
                            : 0;

                          return (
                            <button
                              key={palete.id}
                              onClick={() => navigate('/conferente/pre_carregamento', { state: { numeroPalete: palete.numero_palete } })}
                              className="w-full p-4 hover:bg-green-50 transition text-left active:scale-[0.98]"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-amber-700">
                                    Palete #{palete.numero_palete}
                                  </span>
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    Em andamento
                                  </span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>

                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-xs text-gray-500">
                                  {palete.area_nome}
                                  {palete.area_regiao && ` (${palete.area_regiao})`}
                                </p>
                                {palete.doca && (
                                  <span className="text-xs text-gray-400">| Doca: {palete.doca}</span>
                                )}
                              </div>

                              {palete.volumes_faltantes > 0 && (
                                <p className="text-xs text-orange-600 mb-1">
                                  {palete.volumes_faltantes} volume{palete.volumes_faltantes > 1 ? 's' : ''} faltante{palete.volumes_faltantes > 1 ? 's' : ''}
                                </p>
                              )}

                              <div className="mt-2">
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>{palete.total_volumes}/{palete.total_esperado} etiquetas</span>
                                  <span>{progresso}%</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all ${progresso >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min(progresso, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => navigate('/conferente/historico')}
              className="w-full mt-3 text-center text-base text-green-600 font-medium py-3 hover:underline active:text-green-800"
            >
              Ver todas as conferências
            </button>
          </div>
        )}
      </div>

      {/* Bottom Bar - oculta quando o scroll chega ao final */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4 safe-bottom transition-opacity duration-300 ${
          isAtBottom ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
          <PackageCheck className="w-4 h-4" />
          <span>Confere Ai - Coletor v1.0</span>
        </div>
      </div>
    </div>
  );
};

export default ConferenteDashboard;
